/*
  APIs page for FullStack.cash
*/

import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Container, Row, Col, Card, Button } from 'react-bootstrap'
import apiStack from '../assets/images/bch-api-stack2.jpg'

function Apis () {
  return (
    <>
      <Helmet>
        <title>APIs | FullStack.cash</title>
      </Helmet>

      <Container className='py-5'>
        <Row className='mb-5'>
          <Col md={6}>
            <h2>
              APIs: Production-Ready
              <br /> Blockchain Infrastructure
            </h2>
            <p>
              FullStack.cash provides multiple API endpoints for interacting with
              the Bitcoin Cash blockchain. From high-bandwidth production APIs to
              free rate-limited versions for prototyping, we offer solutions for
              every development need.
            </p>
            <p>
              Our primary APIs use the{' '}
              <a
                href='https://github.com/x402-bch/x402-bch'
                target='_blank'
                rel='noreferrer'
              >
                x402-bch
              </a>{' '}
              and{' '}
              <a
                href='https://github.com/coinbase/x402'
                target='_blank'
                rel='noreferrer'
              >
                x402
              </a>{' '}
              protocols to allow easy access and discoverability for AI Agents.
              Payment for access uses BCH on the Bitcoin Cash blockchain or USDC
              stablecoin on the Base blockchain.
            </p>
          </Col>
          <Col md={6}>
            <img src={apiStack} alt='API Stack' className='img-fluid' />
          </Col>
        </Row>

        <Row>
          <Col>
            <h2 className='text-center mb-4'>API Products</h2>
            <p className='text-center mb-4'>
              Choose the API that best fits your needs. Production applications
              should use the x402-bch API for high bandwidth and reliability.
              Developers building prototypes can use the free rate-limited version.
            </p>
          </Col>
        </Row>

        <Row className='g-4 mb-5'>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>x402-bch.fullstack.cash</strong>
                </Card.Title>
                <Card.Text>
                  High-bandwidth, production-ready API for interacting with the
                  Bitcoin Cash blockchain. Uses the x402-bch protocol for easy
                  access and discoverability for AI Agents. <b>Cost per API call targets $0.001 USD.</b> Payment accepts BCH today, with plans to
                  support USDC stablecoin via the standard x402 protocol.
                </Card.Text>
                <div className='d-flex gap-2'>
                  <Button
                    href='https://x402-bch.fullstack.cash'
                    target='_blank'
                    rel='noreferrer'
                    variant='primary'
                  >
                    API
                  </Button>
                  <Button
                    href='https://github.com/x402-bch/x402-bch'
                    target='_blank'
                    rel='noreferrer'
                    variant='outline-primary'
                  >
                    x402-bch Protocol
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>x402.fullstack.cash</strong>
                </Card.Title>
                <Card.Text>
                  High-bandwidth, production-ready API for interacting with the
                  Bitcoin Cash blockchain. Uses the standard{' '}
                  <a
                    href='https://github.com/coinbase/x402'
                    target='_blank'
                    rel='noreferrer'
                  >
                    x402 protocol
                  </a>{' '}
                  for easy access and discoverability for AI Agents.{' '}
                  <b>Cost per API call targets $0.001 USD.</b> Payment accepts USDC
                  on the Base blockchain.
                </Card.Text>
                <div className='d-flex gap-2'>
                  <Button
                    href='https://x402.fullstack.cash'
                    target='_blank'
                    rel='noreferrer'
                    variant='primary'
                  >
                    API
                  </Button>
                  <Button
                    href='https://github.com/coinbase/x402'
                    target='_blank'
                    rel='noreferrer'
                    variant='outline-primary'
                  >
                    x402 Protocol
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>bch.fullstack.cash</strong>
                </Card.Title>
                <Card.Text>
                  Free but highly rate-limited version of x402-bch.fullstack.cash.
                  Perfect for building software prototypes, exercising the{' '}
                  <a
                    href='https://github.com/Permissionless-Software-Foundation/psf-js-examples'
                    target='_blank'
                    rel='noreferrer'
                  >
                    psf-js-examples
                  </a>{' '}
                  example scripts, or making payments to enable access to the x402
                  protocol.
                </Card.Text>
                <Button
                  href='https://bch.fullstack.cash'
                  target='_blank'
                  rel='noreferrer'
                  variant='outline-primary'
                >
                  API
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>free-bch.fullstack.cash</strong>
                </Card.Title>
                <Card.Text>
                  An instance of{' '}
                  <a
                    href='https://cashstack.info/docs/local-back-end/ipfs-bch-wallet-consumer'
                    target='_blank'
                    rel='noreferrer'
                  >
                    ipfs-bch-wallet-consumer
                  </a>
                  , which provides wallet-optimized API access. This API is
                  provided for free, but with no guarantees about uptime. This is part of
                  the{' '}
                  <a
                    href='https://cashstack.info'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Cash Stack
                  </a>
                  , which uses IPFS to greatly reduce the cost of providing access
                  to blockchain infrastructure.
                </Card.Text>
                <div className='d-flex gap-2'>
                  <Button
                    href='https://free-bch.fullstack.cash'
                    target='_blank'
                    rel='noreferrer'
                    variant='outline-primary'
                  >
                    API
                  </Button>
                  <Button
                    href='https://consumers.psfoundation.info/consumers.json'
                    target='_blank'
                    rel='noreferrer'
                    variant='outline-primary'
                  >
                    More Instances
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className='mb-5 py-4' style={{ backgroundColor: '#e9ecef' }}>
              <Row>
                <Col>
                  <h2 className='text-center mb-4'>Which API To Use?</h2>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div style={{ paddingLeft: '15px', paddingRight: '15px' }}>
                    <p className='mb-3'>
                      Here is a quick guide on how to choose the best API for your use case:
                    </p>
                    <div className='mb-4'>
                      <h4 className='h6 mb-2'><strong>Student:</strong></h4>
                      <p className='mb-3 ms-3'>
                        Dev or Student exploring BCH for the first time: Use <a href='https://bch.fullstack.cash/' target='_blank' rel='noreferrer'>bch.fullstack.cash</a> to run through the simple <a href='https://github.com/Permissionless-Software-Foundation/psf-js-examples' target='_blank' rel='noreferrer'>code examples</a>
                      </p>
                    </div>
                    <div className='mb-4'>
                      <h4 className='h6 mb-2'><strong>Developer:</strong></h4>
                      <p className='mb-3 ms-3'>
                        Dev or entrepreneur prototyping a product idea: Use <a href='https://free-bch.fullstack.cash/' target='_blank' rel='noreferrer'>free-bch.fullstack.cash</a> or a <a href='https://woodcashbox.com' target='_blank' rel='noreferrer'>Cash Box</a>
                      </p>
                    </div>
                    <div className='mb-4'>
                      <h4 className='h6 mb-2'><strong>Business:</strong></h4>
                      <p className='mb-3 ms-3'>
                        Company or production app: Use <a href='https://x402-bch.fullstack.cash/' target='_blank' rel='noreferrer'>x402-bch.fullstack.cash</a> (BCH payments) or <a href='https://x402.fullstack.cash/' target='_blank' rel='noreferrer'>x402.fullstack.cash</a> (USDC on Base) or <a href='https://fullstack.cash/pricing' target='_blank' rel='noreferrer'>private infrastructure</a>
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <h2 className='text-center mb-4'>JavaScript Libraries and Examples</h2>
            <p className='text-center mb-4'>
              The following JavaScript libraries and collection of short examples
              are provided to help developers familiarize themselves with API
              usage. They provide many common use cases for working with BCH,
              including checking balances, sending money, creating and sending SLP
              tokens, writing text into transactions using the OP_RETURN output, and
              many more use cases.
            </p>
            <p className='text-center mb-4'>
              Even if you are not working with JavaScript as a programming language,
              you can feed these examples into your AI to convert them to a
              language of your choice.
            </p>
          </Col>
        </Row>

        <Row className='g-4'>
          <Col md={4}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>psf-js-examples</strong>
                </Card.Title>
                <Card.Text>
                  A GitHub repository with a collection of small example code
                  snippets for common use cases. These examples demonstrate how to
                  use the FullStack.cash APIs for various Bitcoin Cash operations.
                  Perfect for learning and as a reference for your own projects.
                </Card.Text>
                <Button
                  href='https://github.com/Permissionless-Software-Foundation/psf-js-examples'
                  target='_blank'
                  rel='noreferrer'
                  variant='outline-primary'
                >
                  Examples
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>minimal-slp-wallet</strong>
                </Card.Title>
                <Card.Text>
                  This library provides basic wallet functionality. It can leverage
                  all the APIs above, both free and paid. psf-js-examples contains
                  examples for using this library. It's the core engine behind the{' '}
                  <a
                    href='https://wallet.psfoundation.info'
                    target='_blank'
                    rel='noreferrer'
                  >
                    wallet.psfoundation.info
                  </a>{' '}
                  React web wallet.
                </Card.Text>
                <Button
                  href='https://www.npmjs.com/package/minimal-slp-wallet'
                  target='_blank'
                  rel='noreferrer'
                  variant='outline-primary'
                >
                  Library
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>
                  <strong>bch-js</strong>
                </Card.Title>
                <Card.Text>
                  This is a swiss army knife library for working with BCH. It
                  contains low-level cryptographic primitives for deriving addresses,
                  creating custom transactions, and more. This library is embedded
                  in minimal-slp-wallet, so by using that library you have both
                  libraries.
                </Card.Text>
                <Button
                  href='https://www.npmjs.com/package/@psf/bch-js'
                  target='_blank'
                  rel='noreferrer'
                  variant='outline-primary'
                >
                  Library
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Apis
